/**
 * Utilidad para ordenar y filtrar datos de tablas (mismo comportamiento que Campanas).
 * Uso: filterData(data, searchTerm, searchableFields), getSortedData(filteredData, sortColumn, sortDirection)
 */

export function filterData(dataToFilter, searchTerm, searchableFieldsGetter) {
  if (!searchTerm || !searchTerm.trim()) return dataToFilter;
  const searchLower = searchTerm.toLowerCase().trim();
  return dataToFilter.filter(item => {
    const fields = typeof searchableFieldsGetter === 'function' ? searchableFieldsGetter(item) : searchableFieldsGetter;
    return fields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
  });
}

/** Convierte valores típicos de API (YYYY-MM-DD, ISO, epoch) a ms; null si no es fecha usable. */
function dateToMillis(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && Number.isFinite(val)) {
    return val < 1e12 ? val * 1000 : val;
  }
  const s = String(val).trim();
  if (!s || s === '0000-00-00' || s.startsWith('0000-00-00')) return null;
  const datePart = s.includes(' ') ? s.split(' ')[0] : s;
  if (/^\d{4}-\d{2}-\d{2}/.test(datePart)) {
    const t = Date.parse(`${datePart.slice(0, 10)}T12:00:00`);
    return Number.isNaN(t) ? null : t;
  }
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) {
    const [d, m, y] = s.split('-');
    const t = Date.parse(`${y}-${m}-${d}T12:00:00`);
    return Number.isNaN(t) ? null : t;
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

/**
 * Ordena filas por columna: numérico (parseFloat), fecha (dateColumns), o texto (localeCompare es).
 * @param {object[]} dataToSort
 * @param {string|null} sortColumn
 * @param {'asc'|'desc'} sortDirection
 * @param {{ dateColumns?: string[] }} [options] — claves de columnas a tratar como fecha
 */
export function sortData(dataToSort, sortColumn, sortDirection, options = {}) {
  const list = dataToSort || [];
  if (!sortColumn || !list.length) return list;
  const dateSet = new Set(options.dateColumns || []);
  return [...list].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];

    if (dateSet.has(sortColumn)) {
      const at = dateToMillis(aValue);
      const bt = dateToMillis(bValue);
      const aNull = at == null;
      const bNull = bt == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      const cmp = at - bt;
      return sortDirection === 'asc' ? cmp : -cmp;
    }

    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';
    const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
    const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
    const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
    const comparison = isNumeric
      ? aNum - bNum
      : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

/**
 * Filtros de columna estilo Excel: cada clave tiene la lista de etiquetas permitidas.
 * skipColumnKey omite ese filtro (para listar valores aún visibles si se quitara).
 * getLabel(row, key) debe devolver la misma etiqueta que se elige en el menú.
 */
export function applyColumnFilters(rows, columnFilters, getLabel, skipColumnKey) {
  const list = rows || [];
  const entries = Object.entries(columnFilters || {}).filter(
    ([k, allowed]) => k !== skipColumnKey && Array.isArray(allowed) && allowed.length > 0,
  );
  if (!entries.length) return list;
  const labelOf =
    typeof getLabel === 'function'
      ? getLabel
      : (row, key) => {
          const v = row?.[key];
          if (v === null || v === undefined || v === '') return '(Vacío)';
          return String(v);
        };
  return list.filter((r) =>
    entries.every(([key, allowed]) => allowed.includes(labelOf(r, key))),
  );
}
